import { friendlyGeneName } from '@resistapp/client/components/selected-map-sampling-point/site-details/site-details-utils';
import { OverviewDatum } from '@resistapp/client/data-utils/plot-data/build-overview-line-data';
import { getGenesAndCopyNumbers } from '@resistapp/client/data-utils/plot-data/process-overview-line-datum';
import { GetGroup, l1TargetByL2, L1Targets, L2Targets } from '@resistapp/common/assays';
import { MinorTarget } from '@resistapp/common/assays-temp-96-gene-minor-targets';
import { friendlyCopyNumber, friendlyL2Target } from '@resistapp/common/friendly';
import {
  InsightBullet,
  InsightSelect,
  KeyInsightCategory,
  KeyInsights,
  KeyInsightValence,
} from '@resistapp/common/key-insights/types';
import { LOQ_COPY_NUMBER_IN_L } from '@resistapp/common/statistics/fold-change';
import { FullAbundance, MetricMode, ProcessMode } from '@resistapp/common/types';
import { chain, get, isFinite, maxBy, mean } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// const REDUCTION_GOOD_THRESHOLD = -2;
const REDUCTION_BAD_THRESHOLD = -1;

// interface OverallStats {
//   average: number | null;
//   bestValue: number | null;
//   bestDate: Date | null;
//   worstValue: number | null;
//   worstDate: Date | null;
// }

interface TargetStats {
  targetIdentifier: L2Targets | MinorTarget | string; // The L2Target, MinorTarget, or Assay string being analyzed
  averageReduction: number | null;
  averageAfterCopies: number | null;
  afterDetectedMonths: number;
  totalMonths: number;
}

export function computeKeyInsights(
  siteSeries: OverviewDatum[],
  processMode: ProcessMode,
  getGroup: GetGroup,
  originalSiteSmplPntId: number,
  metricMode: MetricMode = MetricMode.REDUCTION,
): KeyInsights | null {
  if (!siteSeries.length || metricMode !== MetricMode.REDUCTION) {
    return null;
  }

  const siteSmplPntId = originalSiteSmplPntId;
  const bullets: InsightBullet[] = [];

  // 1. OVERALL REDUCTION (lead bullet)
  // DO NOT REMOVE IN CASE WE NEED IT AGAIN
  // const overallStats = getOverallReductionStats(
  //   siteSeries,
  //   // processMode is no longer passed
  //   metricMode,
  //   getGroup,
  // );
  // if (overallStats.average !== null) {
  //   const avgText = formatLog10Reduction(overallStats.average);
  //   const avgPercent = formatReductionPercentage(overallStats.average);
  //   const valence =
  //     overallStats.average <= REDUCTION_GOOD_THRESHOLD
  //       ? KeyInsightValence.POSITIVE
  //       : overallStats.average >= REDUCTION_BAD_THRESHOLD
  //         ? KeyInsightValence.NEGATIVE
  //         : KeyInsightValence.NEUTRAL;
  //   bullets.push(
  //     createBullet(KeyInsightCategory.OVERALL, valence, `Average 10-fold reduction is ${avgText} ${avgPercent}.`),
  //   );
  // }

  // 2. ANTIBIOTIC GROUP WITH BEST REDUCTION
  const sortedAntibioticStats = chain(siteSeries[0].beforeAbundances ?? [])
    .map(abundance => getGroup(abundance.assay, 'l2Target') as L2Targets | undefined)
    .compact()
    .uniq()
    .filter(l2 => l1TargetByL2[l2] === L1Targets.ARG)
    .map(target => getTargetStats(siteSeries, target, getGroup))
    .filter(s => s.averageReduction !== null && isFinite(s.averageReduction))
    .sortBy(s => s.averageReduction)
    .value();
  if (sortedAntibioticStats.length > 0) {
    const bestAntibiotic = sortedAntibioticStats[0];
    const bestL2 = bestAntibiotic.targetIdentifier as unknown as L2Targets;
    const bestReductionText = formatLog10Reduction(bestAntibiotic.averageReduction);
    const bestReductionPercent = formatReductionPercentage(bestAntibiotic.averageReduction);
    bullets.push(
      createBullet(
        KeyInsightCategory.ANTIBIOTIC,
        KeyInsightValence.POSITIVE,
        `${friendlyL2Target(bestL2)} resistance genes show the best average reduction of ${bestReductionText} ${bestReductionPercent}.`,
        { l2Target: bestL2 },
      ),
    );
  }

  // 3. HOSPITAL-ACQUIRED INFECTION (HAI) PATHOGENS - main bullet with sub-bullets
  const haiAssays = [
    'AY473', // A. baumannii
    'AY478', // K. pneumoniae
    'AY479', // P. aeruginosa
    'AY631', // E. coli
    // 'AY626', // S. typhi
    // 'AY480', // Staphylococci
    // 'AY629', // C. albicans
    // 'AY625', // Shigella
    // 'AY632', // Legionella
  ];

  const analysedPathogenAssays = chain(siteSeries[0].beforeAbundances ?? [])
    .map(a => a.assay)
    .filter(a => getGroup(a, 'l2Target') === L2Targets.PATHOGEN_MARKER)
    .uniq()
    .value();
  const analysedHaiAssays = haiAssays.filter(assay => analysedPathogenAssays.includes(assay));

  const meanAfterCopiesInL = (datum: OverviewDatum, assay: string): number => {
    return chain(datum.afterAbundances || [])
      .filter(a => a.assay === assay)
      .map(a => a.copiesPerL ?? LOQ_COPY_NUMBER_IN_L)
      .mean()
      .value();
  };

  // Calculate detection status for the overall HAI bullet
  const monthsWithAnyHaiDetection = siteSeries
    .map(datum => {
      const isAnyDetected = analysedHaiAssays.some(assay => {
        const copyNumber = meanAfterCopiesInL(datum, assay);
        return copyNumber > LOQ_COPY_NUMBER_IN_L;
      });
      return isAnyDetected;
    })
    .filter(detected => detected).length; // Count months where any were detected
  const totalMonths = siteSeries.length;
  const mainHaiText =
    monthsWithAnyHaiDetection === 0 && totalMonths > 0
      ? `Hospital-Acquired Infection (HAI) pathogens were not detected in treated WW.`
      : monthsWithAnyHaiDetection === totalMonths
        ? `Hospital-Acquired Infection (HAI) pathogens were detected in treated WW every month.`
        : `Hospital-Acquired Infection (HAI) pathogens were detected in treated WW in ${monthsWithAnyHaiDetection} / ${totalMonths} months.`;

  const haiChildren: InsightBullet[] = analysedHaiAssays
    .map(assay => {
      const copyNumbersPerMonth = siteSeries.map(d => meanAfterCopiesInL(d, assay));
      const numericCopyNumbers: number[] = copyNumbersPerMonth.filter(
        (cn): cn is number => typeof cn === 'number' && isFinite(cn),
      );
      const totalPoints = siteSeries.length;
      if (totalPoints === 0) return undefined;
      const detectedPoints = numericCopyNumbers.filter(cn => cn > LOQ_COPY_NUMBER_IN_L).length;
      const geneName = friendlyGeneName(
        assay,
        get(
          siteSeries
            .find(d => d.afterAbundances?.some(a => a.assay === assay))
            ?.afterAbundances?.find(a => a.assay === assay),
          'gene',
          assay,
        ),
      );
      const bulletText =
        detectedPoints === 0 && totalPoints > 0
          ? `${geneName} was not detected in treated WW.`
          : detectedPoints === totalPoints
            ? `${geneName} was detected in treated WW every month!`
            : `${geneName} was detected in treated WW in ${detectedPoints} / ${totalPoints} months.`;
      return createBullet(KeyInsightCategory.PATHOGEN, KeyInsightValence.NEUTRAL, bulletText, { assay });
    })
    .filter(b => !!b);

  bullets.push(
    createBullet(
      KeyInsightCategory.PATHOGEN,
      monthsWithAnyHaiDetection > 0 ? KeyInsightValence.NEGATIVE : KeyInsightValence.POSITIVE,
      mainHaiText,
      undefined,
      haiChildren,
    ),
  );

  // 4. CHALLENGES — Nest existing challenges under a main bullet
  const challengeChildren: InsightBullet[] = [];

  // 4.1 Antibiotic challenge — combine remaining abundance and reduction (geometric scoring)
  // Primary: candidates with effluent detections in ≥50% months
  const abundantCandidates = sortedAntibioticStats.filter(s => {
    return (
      s.totalMonths > 0 &&
      s.afterDetectedMonths / s.totalMonths >= 0.5 &&
      s.averageAfterCopies !== null &&
      isFinite(s.averageAfterCopies)
    );
  });

  // Secondary: any effluent detection at all
  const fallbackCandidates = abundantCandidates.length
    ? abundantCandidates
    : sortedAntibioticStats.filter(s => s.afterDetectedMonths > 0 && s.averageAfterCopies !== null);

  const candidateByScore = [...fallbackCandidates].sort(
    (a, b) => computeAntibioticChallengeScore(b) - computeAntibioticChallengeScore(a),
  );

  // Final fallback: worst reduction above threshold (previous behavior)
  const reductionFallback: TargetStats | undefined = [...sortedAntibioticStats]
    .reverse()
    .find(s => s.averageReduction !== null && s.averageReduction >= REDUCTION_BAD_THRESHOLD);

  const challengeAntibiotic: TargetStats | undefined = candidateByScore.length
    ? candidateByScore[0]
    : reductionFallback;
  if (challengeAntibiotic !== undefined) {
    const l2 = challengeAntibiotic.targetIdentifier as unknown as L2Targets;
    const avgCopies =
      challengeAntibiotic.averageAfterCopies !== null && isFinite(challengeAntibiotic.averageAfterCopies)
        ? friendlyCopyNumber(challengeAntibiotic.averageAfterCopies)
        : 'N/A';
    const reductionText = formatLog10Reduction(challengeAntibiotic.averageReduction);
    const reductionPercentPlain = formatReductionPercentagePlain(challengeAntibiotic.averageReduction);

    challengeChildren.push(
      createBullet(
        KeyInsightCategory.CHALLENGE,
        KeyInsightValence.NEGATIVE,
        `${friendlyL2Target(l2)} ARGs remained abundant in treated WW with avg. ${avgCopies} copies/L (reduction ${reductionText}, ${reductionPercentPlain}).`,
        { l2Target: l2 },
      ),
    );
  }

  // 4.2 Non-HAI Pathogen challenge - highest average after copies among non-HAI pathogens
  const nonHaiPathogenAssaysInData = analysedPathogenAssays.filter(assay => !haiAssays.includes(assay));
  const nonHaiPathogenStats = nonHaiPathogenAssaysInData.map(assay => getTargetStats(siteSeries, assay, getGroup));
  const worstNonHaiPathogen = maxBy(nonHaiPathogenStats, s => s.averageAfterCopies ?? -Infinity);

  if (
    worstNonHaiPathogen &&
    worstNonHaiPathogen.averageAfterCopies !== null &&
    isFinite(worstNonHaiPathogen.averageAfterCopies)
  ) {
    const geneName = friendlyGeneName(
      worstNonHaiPathogen.targetIdentifier,
      get(
        siteSeries
          .find(d => d.afterAbundances?.some(a => a.assay === worstNonHaiPathogen.targetIdentifier))
          ?.afterAbundances?.find(a => a.assay === worstNonHaiPathogen.targetIdentifier),
        'gene',
        worstNonHaiPathogen.targetIdentifier,
      ),
    );
    const avgCopies = friendlyCopyNumber(worstNonHaiPathogen.averageAfterCopies);
    challengeChildren.push(
      createBullet(
        KeyInsightCategory.CHALLENGE,
        KeyInsightValence.NEGATIVE,
        `${geneName} remained abundant in treated WW with avg. ${avgCopies} copies/L.`,
        { assay: worstNonHaiPathogen.targetIdentifier },
      ),
    );
  }

  // 4.3 Pollution marker (MST) challenge - highest average after copies
  const mstAssays = chain(siteSeries)
    .flatMap(d => getGenesAndCopyNumbers(d, MinorTarget.MST, MetricMode.REDUCTION, processMode, getGroup))
    .map(g => g.assay)
    .uniq()
    .filter(assay => assay !== 'AY636') // Do not consider the universal Bacteroidales assay
    .value();
  const mstStats = mstAssays.map(assay => getTargetStats(siteSeries, assay, getGroup));
  const worstMst = maxBy(mstStats, s => s.averageAfterCopies ?? -Infinity);
  if (worstMst && worstMst.averageAfterCopies !== null && isFinite(worstMst.averageAfterCopies)) {
    const geneName = get(
      siteSeries
        .find(d => d.afterAbundances?.some(a => a.assay === worstMst.targetIdentifier))
        ?.afterAbundances?.find(a => a.assay === worstMst.targetIdentifier),
      'gene',
      worstMst.targetIdentifier,
    );
    // Create child bullet
    challengeChildren.push(
      createBullet(
        KeyInsightCategory.CHALLENGE,
        KeyInsightValence.NEGATIVE,
        `Human fecal marker ${geneName} remained detectable in treated samples.`,
        { assay: worstMst.targetIdentifier }, // Keep select action
      ),
    );
  }

  // Only add the main Challenges bullet if there are child bullets
  if (challengeChildren.length > 0) {
    bullets.push(
      createBullet(
        KeyInsightCategory.CHALLENGE,
        KeyInsightValence.NEGATIVE,
        `Challenges:`, // Main bullet text
        undefined, // No select action for main bullet
        challengeChildren, // Attach children
      ),
    );
  }

  if (!bullets.length) {
    return null;
  }

  return {
    siteSmplPntId,
    metricMode,
    bullets,
  };
}

function createBullet(
  category: KeyInsightCategory,
  valence: KeyInsightValence,
  text: string,
  select?: InsightSelect,
  children?: InsightBullet[],
): InsightBullet {
  // Explicitly typing the result of uuidv4()
  const id: string = uuidv4();
  return { id, category, valence, text, select, children };
}

// Simple helper for formatting log10 reduction values to one decimal place
function formatLog10Reduction(value: number | null): string {
  if (value === null || !isFinite(value)) {
    return 'N/A';
  }
  return value.toFixed(1);
}

// Helper to calculate and format percentage reduction from log10 value
function formatReductionPercentage(value: number | null): string {
  if (value === null || !isFinite(value)) {
    return ''; // Return empty string if value is invalid
  }
  // Calculate percentage: (1 - 10^logValue) * 100
  const percentage = (1 - Math.pow(10, value)) * 100;
  // Handle near-zero percentages
  if (Math.abs(percentage) < 0.01) {
    return '(-0.0%)';
  }
  // Format to one decimal place and wrap in parentheses
  const formattedPercent = percentage.toFixed(1);
  return `(-${formattedPercent}%)`;
}

// Percentage text without surrounding parentheses and with direction
function formatReductionPercentagePlain(value: number | null): string {
  if (value === null || !isFinite(value)) {
    return 'N/A';
  }
  const percentage = (1 - Math.pow(10, value)) * 100;
  const abs = Math.abs(percentage).toFixed(1);
  const direction = percentage >= 0 ? 'decrease' : 'increase';
  return `${abs}% ${direction}`;
}

// Score antibiotics by combining abundance and low reduction.
// Higher score means more challenging: abundant and poorly reduced.
function computeAntibioticChallengeScore(stats: TargetStats): number {
  const after = stats.averageAfterCopies;
  const red = stats.averageReduction;
  // Normalise abundance: log10(copies/L). Use LOQ when missing to avoid NaNs.
  const abundanceLog10 = after && isFinite(after) ? Math.log10(after) : Math.log10(LOQ_COPY_NUMBER_IN_L);
  // Map reduction: negative values are good; penalise values near 0 or positive.
  // Use (1 - clamp(-red, 0, 3)/3) so -3 => 0 (good), 0 => 1 (bad), +0.5 => 1.17 (worse)
  const reductionPenalty = 1 - Math.min(Math.max(-(red ?? 0), 0), 3) / 3;
  return 0.4 * abundanceLog10 + 0.6 * reductionPenalty;
}

// function getOverallReductionStats(
//   siteSeries: OverviewDatum[],
//   // processMode is no longer needed here, we fetch BEFORE and AFTER explicitly
//   metricMode: MetricMode,
//   getGroup: GetGroup,
// ): OverallStats {
//   const reductionsPerDate = siteSeries.map(datum => {
//     const meanBefore = getMeanCopiesPerL(datum, L1Targets.ARG, metricMode, ProcessMode.BEFORE, getGroup);
//     const meanAfter = getMeanCopiesPerL(datum, L1Targets.ARG, metricMode, ProcessMode.AFTER, getGroup);

//     // Calculate log10 reduction only if both before and after means are valid numbers
//     if (meanBefore !== null && meanAfter !== null && meanBefore > 0) {
//       // Use LOQ for after if it's 0 but before was > 0
//       const effectiveAfter = meanAfter === 0 ? LOQ_COPY_NUMBER_IN_L : meanAfter;
//       return Math.log10(effectiveAfter / meanBefore);
//     } else if (meanBefore === null && meanAfter !== null && meanAfter > 0) {
//       // If only after is detected, reduction is effectively log10(meanAfter / LOQ)
//       return Math.log10(meanAfter / LOQ_COPY_NUMBER_IN_L);
//     } else if (meanBefore !== null && meanBefore > 0 && meanAfter === null) {
//       // If only before is detected, reduction is effectively log10(LOQ / meanBefore)
//       return Math.log10(LOQ_COPY_NUMBER_IN_L / meanBefore);
//     }
//     // Cannot calculate reduction if neither is valid or before is 0
//     return null;
//   });

//   const validReductionsWithDates = siteSeries
//     .map((datum, i) => ({ date: datum.date, reduction: reductionsPerDate[i] }))
//     .filter(item => item.reduction !== null && isFinite(item.reduction))
//     .map(item => ({ date: new Date(item.date), reduction: item.reduction as number }));

//   if (!validReductionsWithDates.length) {
//     return { average: null, bestValue: null, bestDate: null, worstValue: null, worstDate: null };
//   }

//   const average = mean(validReductionsWithDates.map(item => item.reduction));
//   const best = minBy(validReductionsWithDates, item => item.reduction);
//   const worst = maxBy(validReductionsWithDates, item => item.reduction);

//   return {
//     average,
//     bestValue: best?.reduction ?? null,
//     bestDate: best?.date ?? null,
//     worstValue: worst?.reduction ?? null,
//     worstDate: worst?.date ?? null,
//   };
// }

// Function to calculate the mean copiesPerL for a specific target group and process mode
// function getMeanCopiesPerL(
//   datum: OverviewDatum,
//   target: L1Target | L2Target | MinorTarget,
//   metricMode: MetricMode,
//   processMode: ProcessMode,
//   getGroup: GetGroup,
// ): number | null {
//   // Use L1Target | L2Target | MinorTarget for the target type
//   const genes = getGenesAndCopyNumbers(datum, target, metricMode, processMode, getGroup, true);
//   const validCopies = genes.map(g => g.copiesPerL).filter((c): c is number => !isNil(c) && isFinite(c));
//   // If no valid copies detected for ARG in this mode, we can't calculate reduction for this date
//   // Returning 0 might skew the average if some dates have no detection.
//   // Consider if LOQ should be used as replacement if *any* normalisation happened.
//   // For now, return null if no valid copiesPerL found for ARG.
//   if (!validCopies.length) {
//     // Check if *any* gene was normalised to determine if LOQ replacement is appropriate
//     const anyNormalised = getGenesAndCopyNumbers(
//       datum,
//       L1Targets.REFERENCE,
//       metricMode,
//       processMode,
//       getGroup,
//       true,
//     ).some(g => !isNil(g.copiesPerL) && isFinite(g.copiesPerL));
//     return anyNormalised ? LOQ_COPY_NUMBER_IN_L : null;
//   }
//   // Use mean, treat undetected as 0 implicitly by the filter above unless LOQ needed
//   return mean(validCopies) || (validCopies.length > 0 ? 0 : null);
// }

// Simplified type for target identifier
function getTargetStats(
  siteSeries: OverviewDatum[],
  targetIdentifier: L2Targets | string, // Keep this specific
  getGroup: GetGroup,
): TargetStats {
  const reductions: number[] = [];
  const afterDetectedCopies: number[] = [];

  const totalMonths = siteSeries.length;
  const afterDetectedMonths = siteSeries.reduce((acc, datum) => {
    const afterStats = getMeanCopiesPerLAndDetected(datum.afterAbundances ?? [], targetIdentifier, getGroup);
    return acc + (afterStats.detected ? 1 : 0);
  }, 0);

  siteSeries.forEach(datum => {
    const before = getMeanCopiesPerLAndDetected(datum.beforeAbundances ?? [], targetIdentifier, getGroup);
    const after = getMeanCopiesPerLAndDetected(datum.afterAbundances ?? [], targetIdentifier, getGroup);

    if (after.detected && isFinite(after.mean as number)) {
      afterDetectedCopies.push(after.mean as number);
    }

    // Skip months where neither influent nor effluent show detection for this target
    if (!before.detected && !after.detected) {
      return;
    }

    const effectiveBefore = before.detected ? (before.mean as number) : LOQ_COPY_NUMBER_IN_L;
    const effectiveAfter = after.detected ? (after.mean as number) : LOQ_COPY_NUMBER_IN_L;
    const reduction = Math.log10(effectiveAfter / effectiveBefore);

    if (isFinite(reduction)) {
      reductions.push(reduction);
    }
  });

  const averageReduction = reductions.length ? mean(reductions) : null;
  const averageAfterCopies = afterDetectedCopies.length ? mean(afterDetectedCopies) : null;

  return {
    targetIdentifier,
    averageReduction,
    averageAfterCopies,
    afterDetectedMonths,
    totalMonths,
  };
}

function getMeanCopiesPerLAndDetected(
  abundances: FullAbundance[],
  targetIdentifier: L2Targets | string, // Keep this specific
  getGroup: GetGroup,
): { mean: number | null; detected: boolean } {
  const values = chain(abundances)
    .filter(a =>
      targetIdentifier.startsWith('AY')
        ? a.assay === targetIdentifier
        : getGroup(a.assay, 'l2Target') === targetIdentifier,
    )
    .map(a => a.copiesPerL)
    .filter((v): v is number => typeof v === 'number' && isFinite(v))
    .value();

  if (!values.length) {
    return { mean: null, detected: false };
  }
  return { mean: mean(values), detected: true };
}
